import Navigo from 'navigo';
import { appStore } from '../store/appStore.js';
import { dataService } from '../services/dataService.js';
import { ENV } from '../config/environment.js';

export class AppRouter {
  private router: Navigo;
  private currentUnsubscribe?: () => void;

  constructor() {
    this.router = new Navigo('/', { hash: false });
    this.setupRoutes();
  }

  private setupRoutes() {
    this.router
      .on('/', () => {
        this.renderHome();
      })
      .notFound(() => {
        this.renderNotFound();
      });
  }

  private googleAuthConfig() {
    return `
      <div id="g_id_onload"
        data-client_id="${ENV.googleClientId}"
        data-hd="thistle.co"
        data-callback="handleGoogleSignIn"
        data-auto_select="true"
        data-itp_support="true">
      </div>
    `;
  }

  private renderHome() {
    // Clean up previous subscription
    this.currentUnsubscribe?.();

    const app = document.getElementById('app');
    if (app) {
      const updateLayout = () => {
        const state = appStore.getState();
        const { isPackLinesExpanded } = state;

        if (isPackLinesExpanded) {
          // Expanded layout - prevent overflow and use full height
          app.innerHTML = `
            ${this.googleAuthConfig()}
            <div class="h-screen bg-gray-100 flex flex-col overflow-hidden">
              <alert-component></alert-component>
              <pack-status-header></pack-status-header>
              <div class="flex-1 overflow-hidden">
                <div class="p-6 h-full flex flex-col space-y-6">
                  <pack-lines-snapshot class="flex-1"></pack-lines-snapshot>
                  <routes-list></routes-list>
                </div>
              </div>
              <pack-lines-drawer></pack-lines-drawer>
              <loading-overlay message="Loading..."></loading-overlay>
              <auth-overlay data-google-client-id="${ENV.googleClientId}"></auth-overlay>
            </div>
          `;
        } else {
          // Normal layout - allow scrolling
          app.innerHTML = `
            ${this.googleAuthConfig()}
            <div class="min-h-screen bg-gray-100">
              <alert-component></alert-component>
              <pack-status-header></pack-status-header>
              <div class="p-6 space-y-6">
                <pack-lines-snapshot></pack-lines-snapshot>
                <routes-list></routes-list>
              </div>
              <pack-lines-drawer></pack-lines-drawer>
              <loading-overlay message="Loading..."></loading-overlay>
              <auth-overlay data-google-client-id="${ENV.googleClientId}"></auth-overlay>
            </div>
          `;
        }
      };

      // Initial render
      updateLayout();

      // Subscribe to state changes to update layout when expand state changes
      const state = appStore.getState();
      let lastExpandState = state.isPackLinesExpanded;
      this.currentUnsubscribe = appStore.subscribe(() => {
        const currentState = appStore.getState();
        if (currentState.isPackLinesExpanded !== lastExpandState) {
          lastExpandState = currentState.isPackLinesExpanded;
          updateLayout();
        }
      });
    }
  }

  private renderNotFound() {
    // Clean up previous subscription
    this.currentUnsubscribe?.();

    const app = document.getElementById('app');
    if (app) {
      app.innerHTML = `
        <div class="min-h-screen bg-gray-100 flex items-center justify-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
            <p class="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
            <a href="/" class="text-emerald-600 hover:text-emerald-800 font-medium">Go back home</a>
          </div>
        </div>
      `;
    }
  }

  public start() {
    this.router.resolve();
  }

  public navigate(path: string) {
    this.router.navigate(path);
  }

  public resolve() {
    this.router.resolve();
  }
}

export const router = new AppRouter();
